import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { API, useAuth } from "../context/AuthContext";
import { Lock, Mail, Bell, Smartphone, ShieldCheck } from "lucide-react";
import NotifySettings from "../components/NotifySettings";

const GROUPS = [
  { title: "SEMPRE ATTIVE", desc: "Comunicazioni indispensabili: non possono essere disattivate.", items: [
    ["security", "Email di sicurezza (verifica, password, accessi sospetti)", true],
    ["billing", "Acquisti, ricevute e fatture", true]] },
  { title: "LA TUA ATTIVITÀ", desc: "Arrivano nel Centro Notifiche e, se attive, come push. Nessuna email singola.", items: [
    ["events", "Eventi a cui partecipi"], ["farm", "Aggiornamenti Rally Farm"], ["daily", "Daily Stage"], ["leaderboards", "Classifiche"], ["challenges", "Sfide tra utenti"]] },
  { title: "COMUNICAZIONI COMMERCIALI", desc: "Disattivate per impostazione predefinita. Richiedono il tuo consenso marketing.", commercial: true, items: [
    ["updates", "Novità e aggiornamenti Rally Love"], ["promotions", "Promozioni"], ["sponsors", "Comunicazioni degli sponsor"], ["newsletter", "Newsletter"]] },
  { title: "CANALI", desc: "Come vuoi ricevere le notifiche.", items: [["push", "Notifiche push"], ["inapp", "Notifiche interne all'app"]] },
];

export default function NotificationSettings() {
  const { user, loading } = useAuth(); const nav = useNavigate();
  const [data, setData] = useState(null); const [log, setLog] = useState([]);
  useEffect(() => { if (loading) return; if (!user) { nav("/login?next=/settings/notifications"); return; }
    axios.get(`${API}/me/comm-prefs`).then(r => setData(r.data)).catch(() => {});
    axios.get(`${API}/me/consent-log`).then(r => setLog(r.data)).catch(() => {}); }, [user, loading]);

  const save = async (patch) => {
    try { const { data: d } = await axios.put(`${API}/me/comm-prefs`, patch); setData(x => ({ ...x, ...d })); toast.success("Preferenze salvate");
      axios.get(`${API}/me/consent-log`).then(r => setLog(r.data)).catch(() => {}); }
    catch { toast.error("Impossibile salvare le preferenze"); }
  };
  if (!data) return <div className="min-h-screen grain-bg"><Navbar /><div className="pt-32 text-center text-slate-500">Caricamento…</div></div>;
  const p = data.prefs;

  return (
    <div className="min-h-screen grain-bg"><Navbar />
      <div className="pt-24 pb-28 max-w-3xl mx-auto px-5" data-testid="notification-settings-page">
        <div className="pill-badge inline-flex mb-3"><Bell className="w-3 h-3" /> PROFILO → IMPOSTAZIONI</div>
        <h1 className="font-display font-black text-3xl md:text-4xl mb-2">Notifiche e comunicazioni</h1>
        <p className="text-slate-400 text-sm mb-8">Decidi tu cosa ricevere. Le attività ordinarie restano nel Centro Notifiche: inviamo email solo quando è davvero necessario.</p>

        <div className={`rl-card p-5 mb-6 flex items-start justify-between gap-4 ${data.marketing_consent ? "border-[#30D158]/40" : ""}`} data-testid="marketing-consent-card">
          <div><div className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FF9F0A]" /> Consenso marketing</div>
            <div className="text-xs text-slate-400 mt-1">Necessario per ricevere promozioni, comunicazioni degli sponsor e newsletter. Puoi revocarlo in qualsiasi momento. Versione testo: {data.text_version}.</div></div>
          <button onClick={() => save({ marketing: !data.marketing_consent })} data-testid="marketing-consent-toggle"
            className={`px-4 py-2 rounded-full font-mono-rl text-xs shrink-0 ${data.marketing_consent ? "bg-[#30D158]/20 text-[#30D158] border border-[#30D158]/50" : "border border-white/15 text-slate-300"}`}>{data.marketing_consent ? "ATTIVO" : "NON ATTIVO"}</button>
        </div>

        {GROUPS.map(g => (
          <section key={g.title} className="rl-card p-5 mb-4" data-testid={`prefs-group-${g.title.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[10px] font-mono-rl tracking-widest text-slate-500 mb-1">{g.title}</div>
            <p className="text-xs text-slate-400 mb-3">{g.desc}</p>
            <div className="space-y-2">
              {g.items.map(([k, label, fixed]) => {
                const on = fixed ? true : !!p[k]; const disabled = fixed || (g.commercial && !data.marketing_consent);
                return (
                  <button key={k} disabled={disabled} onClick={() => save({ [k]: !p[k] })} data-testid={`pref-${k}`}
                    className={`w-full flex items-center justify-between text-sm px-3 py-2.5 rounded-lg border min-h-[44px] ${on ? "border-[#30D158]/50 bg-[#30D158]/10" : "border-white/10"} disabled:opacity-60`}>
                    <span className="flex items-center gap-2 text-left">{fixed ? <Lock className="w-4 h-4 text-slate-500" /> : k === "push" ? <Smartphone className="w-4 h-4" /> : <Mail className="w-4 h-4" />}{label}</span>
                    <span className="font-mono-rl text-[10px]">{fixed ? "SEMPRE" : on ? "ATTIVE" : "DISATTIVE"}</span>
                  </button>);
              })}
            </div>
          </section>
        ))}

        <section className="rl-card p-5 mb-4" data-testid="digest-card">
          <div className="text-[10px] font-mono-rl tracking-widest text-slate-500 mb-1">RIEPILOGO EMAIL FACOLTATIVO</div>
          <p className="text-xs text-slate-400 mb-3">Al posto di tante email separate: un solo riepilogo con progressi Rally Farm, Daily Stage, eventi, classifica, vantaggi Card e sfide. Non si attiva mai da solo.</p>
          <div className="flex gap-2 flex-wrap">
            {[["none", "Nessun riepilogo"], ["weekly", "Settimanale"], ["monthly", "Mensile"]].map(([v, l]) => (
              <button key={v} onClick={() => save({ digest: v })} data-testid={`digest-${v}`}
                className={`px-4 py-2 rounded-full text-xs font-mono-rl ${p.digest === v ? "bg-[#FF1E27] text-white" : "border border-white/10 text-slate-300"}`}>{l}</button>))}
          </div>
        </section>

        <section className="rl-card p-5 mb-4"><NotifySettings /></section>

        <section className="rl-card p-5" data-testid="consent-log-card">
          <div className="text-[10px] font-mono-rl tracking-widest text-slate-500 mb-3">REGISTRO CONSENSI</div>
          {log.length === 0 && <div className="text-xs text-slate-500">Nessuna modifica registrata.</div>}
          {log.slice(0, 15).map(l => (
            <div key={l.id} className="flex justify-between text-xs py-1.5 border-b border-white/5" data-testid={`consent-${l.id}`}>
              <span>{l.type} → <b className={l.revoke ? "text-[#FF1E27]" : "text-[#30D158]"}>{l.revoke ? "revocato" : "attivato"}</b> <span className="text-slate-500">({l.source}, {l.text_version})</span></span>
              <span className="text-slate-500 font-mono-rl">{new Date(l.at).toLocaleString("it-IT")}</span>
            </div>))}
        </section>
      </div>
    </div>
  );
}
